import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Text } from 'office-ui-fabric-react/lib/Text';
import ProductsList from '../ProductsList';
import DataItem from '../DataItem';
import StackGrid from '../StackGrid';
import Checkbox from '../Checkbox';
import Link from '../Link';
import styles from './BundleVersionListRowDetails.style';

import { formatDateString } from '../../utils';

import { useBundleVersionContext } from '../../context/bundle-version';

const BundleVersionListRowDetails = ({ bundleVersion }) => {
  const { isOpen } = useBundleVersionContext();

  const className = clsx(styles.root, styles.bundleInfo, {
    [styles.isClosed]: !isOpen,
    [styles.isOpen]: isOpen,
  });

  const columns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'icon',
      iconName: 'Tag',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="Tag" ariaLabel="Tag" className={styles.detailRowIconColor} />
          </div>
        );
      },
    },
    {
      key: 'productName',
      name: 'Product Name',
      fieldName: 'productName',
      minWidth: 220,
      maxWidth: 220,
      isResizable: true,
    },
    {
      key: 'tokenId',
      name: 'Token ID',
      fieldName: 'tokenId',
      isResizable: true,
      minWidth: 200,
      maxWidth: 200,
      onRender: item => {
        return <span>#{item.tokenId}</span>;
      },
    },
    {
      key: 'productId',
      name: 'Product ID',
      fieldName: 'productId',
      isResizable: true,
      minWidth: 220,
      maxWidth: 220,
    },
    {
      key: 'isRoyaltyBearing',
      name: 'Royalty Bearing',
      fieldName: 'isRoyaltyBearing',
      isResizable: true,
      minWidth: 70,
      maxWidth: 70,
      onRender: item => {
        return (
          <Checkbox
            disabled
            field={{
              name: 'isRoyaltyBearing',
              value: `${item.isRoyaltyBearing}` === 'true',
            }}
            label=""
            ariaLabel="isRoyaltyBearing"
          />
        );
      },
    },
    {
      key: 'allocationPercentage',
      name: 'Allocation',
      fieldName: 'allocationPercentage',
      isResizable: true,
      minWidth: 50,
      maxWidth: 50,
      onRender: ({ allocationPercentage }) => {
        return `${allocationPercentage}%`;
      },
    },
    {
      key: 'tasks',
      name: 'View',
      isResizable: false,
      styles: { root: { visibility: 'hidden' } },
      minWidth: 70,
      maxWidth: 70,
      onRender: item => {
        return (
          <Link className={styles.viewButton} href={`/products/${item.tokenId}`} target="_blank">
            View
          </Link>
        );
      },
    },
  ];

  return (
    <>
      <div className={className}>
        <Text block variant="medium">
          <strong className={styles.value}>Bundle Date Range</strong>
        </Text>
        <StackGrid smCols={1} mdCols={2} lgCols={2} gap={4} parentClassName={styles.meta}>
          {bundleVersion.startDate && (
            <DataItem
              label={
                bundleVersion.previousAllocation ? 'Revised Start Date' : 'Effective Start Date'
              }
            >
              {formatDateString(bundleVersion.startDate)}
            </DataItem>
          )}
          {bundleVersion.endDate && (
            <DataItem
              label={
                bundleVersion.previousAllocation ? 'Revised Expiration Date' : 'Expiration Date'
              }
            >
              {formatDateString(bundleVersion.endDate)}
            </DataItem>
          )}
        </StackGrid>
        <div className={styles.focusableGrid} data-is-focusable>
          <ProductsList
            columns={columns}
            items={bundleVersion.componentAllocationPercentage}
            shimmered
            disableRowLink
          />
        </div>
      </div>
    </>
  );
};

BundleVersionListRowDetails.propTypes = {
  bundleVersion: PropTypes.shape({
    previousAllocation: PropTypes.object,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    componentAllocationPercentage: PropTypes.arrayOf(
      PropTypes.shape({
        productName: PropTypes.string,
        tokenId: PropTypes.number,
        isRoyaltyBearing: PropTypes.bool,
        allocationPercentage: PropTypes.number,
      }),
    ),
  }),
};

BundleVersionListRowDetails.defaultProps = {
  bundleVersion: {
    previousAllocation: {},
    startDate: '',
    endDate: '',
    componentAllocationPercentage: [],
  },
};

export default BundleVersionListRowDetails;
